/**
 * Единый формат адреса доставки — от частного к общему.
 *
 * Как бы клиент ни задал адрес — набрал руками, поставил маркер на карте, выбрал из
 * «Моих адресов» — строка собирается одинаково для всех:
 *
 *   «кв. M, подъезд N, домофон K, дом, улица, населённый пункт»
 *
 * Порядок именно такой, потому что в шапке и в корзине строка обрезается по ширине:
 * при обратном порядке клиент видел только название населённого пункта, которое и так
 * очевидно, а дом и улицу — нет.
 *
 * «дом, улица, населённый пункт» — «точка» адреса: её отдаёт геокодер и по ней считается
 * зона. Квартира, подъезд и домофон — уточнения из окна карты, они попадают в строку
 * заказа, чтобы курьер видел их сразу. Улицы в посёлках может не быть — пустые части
 * просто пропускаются, порядок остальных не меняется.
 *
 * Геокодер Яндекса разбирает такую строку без потерь (проверено на боевом ключе:
 * точность «дом» и с уточнениями в начале, и без них), поэтому серверная сверка
 * адреса с зоной работает с той же строкой.
 */

export interface AddressPointParts {
  /** Населённый пункт: станица, хутор, посёлок, город. */
  locality?: string | null
  /** Улица с типом, как отдаёт геокодер: «Широкая улица», «квартал Малышок». */
  street?: string | null
  /** Номер дома: «32», «105/1», «7а». */
  house?: string | null
}

export interface AddressDetails {
  apartment?: string | number | null
  entrance?: string | number | null
  intercom?: string | number | null
}

const clean = (value: string | number | null | undefined): string =>
  value == null ? '' : String(value).trim()

/** «дом, улица, населённый пункт» — пустые части пропускаются. */
export function formatPointAddress(parts: AddressPointParts | null | undefined): string {
  if (!parts) return ''
  return [clean(parts.house), clean(parts.street), clean(parts.locality)].filter(Boolean).join(', ')
}

/**
 * Полная строка для заказа: «кв. M, подъезд N, домофон K» и следом точка. Если клиент
 * уже вписал что-то из этого в саму строку (старые адреса вида «Крайняя 6 квартира 43»),
 * второй раз не добавляем.
 */
export function formatDeliveryAddress(
  point: string | null | undefined,
  details: AddressDetails | null | undefined,
): string {
  const base = clean(point)
  const lower = base.toLowerCase()
  const parts: string[] = []

  const apartment = clean(details?.apartment)
  if (apartment && !mentions(lower, ['кв.', 'кв ', 'квартира'], apartment)) {
    parts.push(`кв. ${apartment}`)
  }

  const entrance = clean(details?.entrance)
  if (entrance && !mentions(lower, ['подъезд', 'под.'], entrance)) {
    parts.push(`подъезд ${entrance}`)
  }

  const intercom = clean(details?.intercom)
  if (intercom && !mentions(lower, ['домофон', 'код'], intercom)) {
    parts.push(`домофон ${intercom}`)
  }

  if (base) parts.push(base)
  return parts.join(', ')
}

/** В строке уже есть «<метка> <значение>» — например «кв. 43» или «квартира 43». */
function mentions(lowerText: string, labels: string[], value: string): boolean {
  const lowerValue = value.toLowerCase()
  return labels.some((label) => lowerText.includes(`${label} ${lowerValue}`) || lowerText.includes(`${label}${lowerValue}`))
}
